import { defineStore } from "pinia";

import authApi from "@/services/api/authApi";
import usersApi from "@/services/api/usersApi";

import Api from "@/services/apiService";

import { usePermissionsStore } from "@/stores/permissionsStore";
import { useUserDataStore } from "@/stores/userDataStore";

import { checkAuthenticationStatus } from "@/utils/authUtils";

export const useAuthStore = defineStore("auth", {
  state: () => ({
    isAuthenticated: checkAuthenticationStatus(),
    returnUrl: null,
    isSubmitting: false,
    requiredAgreement: null,
    agreementToken: null,
  }),

  actions: {
    setReturnUrl(url) {
      this.returnUrl = url;
    },

    clearReturnUrl() {
      this.returnUrl = null;
    },

    login() {
      this.isAuthenticated = true;
    },

    clearSessionState() {
      // Чистим куки
      const cookieNames = ["token", "userId", "exp"];
      cookieNames.forEach((name) => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      });

      // Чистим localStorage
      localStorage.clear();

      // Чистим permissionsStore
      usePermissionsStore().clearPermissions();
      useUserDataStore().clearUserData();
      Api.updateToken(null);
    },

    logout() {
      this.clearSessionState();

      this.isAuthenticated = false;
      this.returnUrl = null;
      this.resetAgreementStep();
    },

    resetAgreementStep() {
      this.requiredAgreement = null;
      this.agreementToken = null;
    },

    async handleLoginResponse(data) {
      if (data.agreement_required) {
        this.requiredAgreement = data.agreement;
        this.agreementToken = data.agreement_token;
        return false;
      }
      this.resetAgreementStep();
      await this.applySession(data);
      return true;
    },

    async loginWithCredentials(credentials) {
      this.isSubmitting = true;
      try {
        this.resetAgreementStep();
        this.clearSessionState();
        this.isAuthenticated = false;
        const response = await authApi.create(credentials);
        return await this.handleLoginResponse(response.data);
      } finally {
        this.isSubmitting = false;
      }
    },

    async confirmAgreement(formUrl) {
      this.isSubmitting = true;
      try {
        const response = await authApi.confirmAgreement({
          agreement_token: this.agreementToken,
          agreement_given: true,
          agreement_version: this.requiredAgreement.version,
          form_url: formUrl,
        });
        return await this.handleLoginResponse(response.data);
      } catch (error) {
        if (error.response?.status === 422) this.resetAgreementStep();
        throw error;
      } finally {
        this.isSubmitting = false;
      }
    },

    async loginWithToken(token) {
      this.clearSessionState();
      this.isAuthenticated = false;
      const response = await authApi.loginWithToken(token);
      await this.applySession(response.data);
    },

    async applySession({ token, user_id: userId, exp }) {
      const expires = new Date(exp * 1000).toUTCString();
      document.cookie = `token=${encodeURIComponent(token)}; expires=${expires}; path=/`;
      document.cookie = `userId=${encodeURIComponent(userId)}; expires=${expires}; path=/`;
      document.cookie = `exp=${encodeURIComponent(exp)}; expires=${expires}; path=/`;

      Api.updateToken(token);

      const usersResponse = await usersApi.currentUser();
      const usersData = usersResponse.data.result;

      useUserDataStore().setUserData(usersData);

      await usePermissionsStore().loadPermissions();
      this.login();
    },
  },
});
